import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import { Button, ButtonGroup, TextInput } from "components/common";
import Import from "lib/api/Import";
import Client from "lib/api/Client";
import { UserContext } from "lib/context/UserContext";
import { CLOUD_TYPE_SHORT_LABELS, parseCloudType } from "lib/utils/license";
import { exportRecordsAsCSV } from "lib/utils/csv";

const PAGE_SIZE = 100;

// Local cache of client name/cloud type keyed by client_uuid, joined onto connections
// client-side since the connections endpoint only returns client_uuid.
const clientDetailsCache = new Map();

const CLIENT_FETCH_CHUNK_SIZE = 100;
const CLIENT_FETCH_CONCURRENCY_LIMIT = 3;

async function joinClientDetails(data) {
  const uniqueClientUuids = [...new Set(data.map((c) => c.client_uuid).filter(Boolean))];
  const missing = uniqueClientUuids.filter((uuid) => !clientDetailsCache.has(uuid));

  if (missing.length > 0) {
    const chunks = [];
    for (let i = 0; i < missing.length; i += CLIENT_FETCH_CHUNK_SIZE) {
      chunks.push(missing.slice(i, i + CLIENT_FETCH_CHUNK_SIZE));
    }

    for (let i = 0; i < chunks.length; i += CLIENT_FETCH_CONCURRENCY_LIMIT) {
      const chunkBatch = chunks.slice(i, i + CLIENT_FETCH_CONCURRENCY_LIMIT);
      await Promise.all(
        chunkBatch.map(async (chunk) => {
          try {
            const { data: clients } = await Client.get({ uuid: chunk, limit: chunk.length });
            (clients || []).forEach((clientData) => {
              const cloudType =
                clientData.cloud_type !== undefined
                  ? clientData.cloud_type
                  : parseCloudType(clientData.permissions?.celicense);
              clientDetailsCache.set(clientData.uuid, { name: clientData.name, cloud_type: cloudType });
            });
          } catch (err) {
            console.error("Error fetching client chunk:", err);
          }
        })
      );
    }
  }

  return data.map((c) => {
    const details = clientDetailsCache.get(c.client_uuid);
    return {
      ...c,
      client_name: details?.name || c.client_uuid,
      cloud_type: details?.cloud_type ? CLOUD_TYPE_SHORT_LABELS[details.cloud_type] || details.cloud_type : "",
    };
  });
}

export default function WebSocketsPage() {
  const { user } = useContext(UserContext);
  const [connections, setConnections] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(null);

  async function load(pageOffset = offset) {
    setLoading(true);
    setError(null);
    try {
      const response = await Import.connections({ limit: PAGE_SIZE, offset: pageOffset });
      const data = Array.isArray(response) ? response : response.data || [];
      const pagination = response.pagination || {};
      setTotal(pagination.total ?? null);

      const enriched = await joinClientDetails(data);

      setConnections(enriched);
    } catch (err) {
      setError(err.message || "Failed to load connections.");
    }
    setLoading(false);
  }

  async function exportAllConnectionsAsCSV() {
    setExporting(true);
    setError(null);
    try {
      let allConnections = [];
      let currentOffset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await Import.connections({ limit: PAGE_SIZE, offset: currentOffset });
        const data = Array.isArray(response) ? response : response.data || [];
        const pagination = response.pagination || {};
        allConnections.push(...data);
        currentOffset += data.length;
        hasMore = data.length > 0 && (pagination.total == null || allConnections.length < pagination.total);
      }

      const enriched = await joinClientDetails(allConnections);
      const exportRecords = enriched.map((c) => ({
        connection_id: c.connection_id,
        client_name: c.client_name,
        cloud_type: c.cloud_type,
        scope: c.scope || "",
        client_ip: c.client_ip || "",
        client_domain: c.client_domain || "",
        client_computer: c.client_computer || "",
        client_version: c.client_version || "",
        connected_at: c.connected_at ? new Date(c.connected_at).toLocaleString() : "",
      }));

      await exportRecordsAsCSV(exportRecords, [], "websocket-connections-export.csv");
    } catch (err) {
      setError(err.message || "Failed to export connections.");
    }
    setExporting(false);
  }

  useEffect(() => {
    load(offset);
  }, [offset]);

  useEffect(() => {
    if (!search) {
      setFiltered(connections);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(
      connections.filter(
        (c) =>
          (c.connection_id || "").toLowerCase().includes(q) ||
          (c.client_name || "").toLowerCase().includes(q) ||
          (c.scope || "").toLowerCase().includes(q) ||
          (c.client_ip || "").toLowerCase().includes(q) ||
          (c.client_domain || "").toLowerCase().includes(q) ||
          (c.client_computer || "").toLowerCase().includes(q) ||
          (c.client_version || "").toLowerCase().includes(q)
      )
    );
  }, [search, connections]);

  function goToPage(newOffset) {
    setSearch("");
    setOffset(newOffset);
  }

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = total != null ? Math.ceil(total / PAGE_SIZE) : null;

  if (user?.email !== "johnwelch@deltek.com") {
    return <Redirect to="/clients" />;
  }

  function formatDate(val) {
    if (!val) return "—";
    return new Date(val).toLocaleString();
  }

  return (
    <PageLayout>
      <h1>WebSocket Connections</h1>
      <div className="hr hr--darkest"></div>

      <div className="row">
        <div className="col-xs-12 col-sm-4">
          <TextInput
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by connection, client, scope, IP..."
          />
        </div>
        <div className="col-xs-12 col-sm-2">
          <label style={{ visibility: "hidden", marginBottom: 0 }}>
            <span className="label__span">Refresh</span>
          </label>
          <ButtonGroup>
            <Button onClick={() => load(offset)} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </Button>
            <Button onClick={exportAllConnectionsAsCSV} disabled={loading || exporting}>
              {exporting ? "Exporting..." : "Export"}
            </Button>
          </ButtonGroup>
        </div>
        <div className="col-xs-12 col-sm-6" style={{ display: "flex", alignItems: "flex-end", paddingBottom: "8px" }}>
          {!loading && total != null && (
            <span style={{ color: "#666", fontSize: "0.9em" }}>
              {search
                ? `${filtered.length} match${filtered.length !== 1 ? "es" : ""} on this page`
                : `${offset + 1}–${Math.min(offset + connections.length, total)} of ${total} connection${total !== 1 ? "s" : ""}`}
            </span>
          )}
        </div>
      </div>

      {!loading && totalPages != null && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <Button onClick={() => goToPage(0)} disabled={offset === 0 || loading}>
            &laquo; First
          </Button>
          <Button onClick={() => goToPage(offset - PAGE_SIZE)} disabled={offset === 0 || loading}>
            &lsaquo; Prev
          </Button>
          <span style={{ fontSize: "0.9em", color: "#444" }}>
            Page {currentPage} of {totalPages}
          </span>
          <Button onClick={() => goToPage(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total || loading}>
            Next &rsaquo;
          </Button>
          <Button onClick={() => goToPage((totalPages - 1) * PAGE_SIZE)} disabled={currentPage === totalPages || loading}>
            Last &raquo;
          </Button>
        </div>
      )}

      {error && (
        <div style={{ color: "#ef4444", padding: "12px 0" }}>{error}</div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
          No connections found.
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="table table--bordered">
            <thead>
              <tr>
                <th>Connection ID</th>
                <th>Client</th>
                <th>Cloud Type</th>
                <th>IP</th>
                <th>Domain</th>
                <th>Computer</th>
                <th>Version</th>
                <th>Connected At</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.connection_id}>
                  <td style={{ fontFamily: "monospace", fontSize: "0.8em" }}>{c.connection_id}</td>
                  <td>
                    <a href={`/client/${c.client_uuid}`} target="_blank" rel="noreferrer">
                      {c.client_name}
                    </a>
                  </td>
                  <td>{c.cloud_type || "—"}</td>
                  <td>{c.client_ip || "—"}</td>
                  <td>{c.client_domain || "—"}</td>
                  <td>{c.client_computer || "—"}</td>
                  <td>{c.client_version || "—"}</td>
                  <td>{formatDate(c.connected_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageLayout>
  );
}
